import React, {useRef, useState} from "react";
import {InputTextarea} from "primereact/inputtextarea";
import {resolveUrl} from "../../system/app-config";
import UploadDialog from "../components/upload-dialog";
import WordImportDialog from "../components/word-import-dialog";
import AiPromptDialog from "./ai-prompt-dialog";
import RibbonBar from "./ribbon-bar";

function MarkdownEditor({value, onChange, uploader, rows = 24}) {
    const textareaRef = useRef(null);
    const selectionRef = useRef({start: 0, end: 0});
    const uploadKindRef = useRef("image");
    const [activeInline, setActiveInline] = useState({bold: false, italic: false, strike: false, code: false});
    const [uploadDialogVisible, setUploadDialogVisible] = useState(false);
    const [wordImportVisible, setWordImportVisible] = useState(false);
    const [aiDialogVisible, setAiDialogVisible] = useState(false);

    const computeActiveInline = (current) => {
        const start = current.selectionStart;
        const end = current.selectionEnd;
        const textareaValue = current.value;
        const prev = textareaValue.slice(Math.max(0, start - 3), start);
        const next = textareaValue.slice(end, end + 3);
        return {
            bold: prev.endsWith("**") && next.startsWith("**"),
            italic: prev.endsWith("*") && !prev.endsWith("**") && next.startsWith("*") && !next.startsWith("**"),
            strike: prev.endsWith("~~") && next.startsWith("~~"),
            code: prev.endsWith("`") && next.startsWith("`"),
        };
    };

    const trackSelection = () => {
        const current = textareaRef.current;
        if (!current) return;
        selectionRef.current = {start: current.selectionStart, end: current.selectionEnd};
        setActiveInline(computeActiveInline(current));
    };

    const setValueAndSelection = (next, start, end) => {
        onChange(next);
        requestAnimationFrame(() => {
            const current = textareaRef.current;
            if (!current) return;
            current.focus();
            current.setSelectionRange(start, end);
            selectionRef.current = {start, end};
            setActiveInline(computeActiveInline(current));
        });
    };

    const wrapSelection = (command) => {
        const {start, end} = selectionRef.current;
        const selected = value.slice(start, end) || command.placeholder;
        const next = value.slice(0, start) + command.before + selected + command.after + value.slice(end);
        setValueAndSelection(next, start + command.before.length, start + command.before.length + selected.length);
    };

    const prefixLine = (command) => {
        const {start, end} = selectionRef.current;
        const lineStart = value.lastIndexOf("\n", start - 1) + 1;
        const next = value.slice(0, lineStart) + command.arg + value.slice(lineStart);
        setValueAndSelection(next, lineStart + command.arg.length + (start - lineStart), lineStart + command.arg.length + (end - lineStart));
    };

    const insertValue = (command) => {
        const {start, end} = selectionRef.current;
        const next = value.slice(0, start) + command.value + value.slice(end);
        setValueAndSelection(next, start + command.value.length, start + command.value.length);
    };

    const indentLine = (direction) => {
        const {start, end} = selectionRef.current;
        const lineStart = value.lastIndexOf("\n", Math.max(0, start - 1)) + 1;
        if (direction > 0) {
            const next = value.slice(0, lineStart) + "  " + value.slice(lineStart);
            setValueAndSelection(next, lineStart + 2 + (start - lineStart), lineStart + 2 + (end - lineStart));
            return;
        }
        let remove = 0;
        while (remove < 2 && value[lineStart + remove] === " ") remove += 1;
        if (!remove) return;
        const next = value.slice(0, lineStart) + value.slice(lineStart + remove);
        const caretStart = Math.max(lineStart, start - remove);
        setValueAndSelection(next, caretStart, Math.max(caretStart, end - remove));
    };

    const applyHeading = (level) => {
        const current = textareaRef.current;
        if (!current) return;
        const start = current.selectionStart;
        const lineStart = value.lastIndexOf("\n", Math.max(0, start - 1)) + 1;
        const nextBreak = value.indexOf("\n", lineStart);
        const lineEnd = nextBreak === -1 ? value.length : nextBreak;
        const lineText = value.slice(lineStart, lineEnd);
        const match = lineText.match(/^(#{1,6})\s+/);

        let next;
        if (level === 0) {
            if (!match) return;
            next = value.slice(0, lineStart) + lineText.slice(match[0].length) + value.slice(lineEnd);
        } else {
            const body = match ? lineText.slice(match[0].length) : lineText;
            next = value.slice(0, lineStart) + `${"#".repeat(level)} ${body}` + value.slice(lineEnd);
        }
        const activeLineEnd = next.indexOf("\n", lineStart) === -1 ? next.length : next.indexOf("\n", lineStart);
        setValueAndSelection(next, activeLineEnd, activeLineEnd);
    };

    const runCommand = (button) => {
        const command = button.cmd;
        switch (command.type) {
            case "wrap":
                wrapSelection(command);
                return;
            case "line":
                prefixLine(command);
                return;
            case "insert":
                insertValue(command);
                return;
            case "indent":
                indentLine(1);
                return;
            case "outdent":
                indentLine(-1);
                return;
            case "heading":
                applyHeading(command.arg);
                return;
            case "upload":
                uploadKindRef.current = command.kind;
                setUploadDialogVisible(true);
                return;
            case "word":
                setWordImportVisible(true);
                return;
            case "ai":
                setAiDialogVisible(true);
                return;
            default:
                return;
        }
    };

    const handleUploaded = (uploaded) => {
        const file = Array.isArray(uploaded) ? uploaded[0] : uploaded;
        if (!file?.url) return;
        const url = resolveUrl(file.url);
        const name = (file.name || "file").replace(/\.[^.]+$/, "");
        const {start, end} = selectionRef.current;
        const snippet = uploadKindRef.current === "video"
            ? `[${name}](${url})`
            : `![${name}](${url})`;
        const next = value.slice(0, start) + snippet + value.slice(end);
        setValueAndSelection(next, start + snippet.length, start + snippet.length);
        setUploadDialogVisible(false);
    };

    const handleAiComplete = (markdown) => {
        const text = String(markdown || "").trim();
        if (!text) return;
        const {start, end} = selectionRef.current;
        const next = value.slice(0, start) + text + value.slice(end);
        setValueAndSelection(next, start + text.length, start + text.length);
    };

    return (
        <div className="w-full rtl">
            <RibbonBar onCommand={runCommand} active={activeInline}/>
            <InputTextarea
                ref={textareaRef}
                className="w-full text-xs ltr"
                rows={rows}
                value={value ?? ""}
                onChange={(event) => {
                    onChange(event.target.value);
                    trackSelection();
                }}
                onFocus={trackSelection}
                onClick={trackSelection}
                onKeyUp={trackSelection}
                onSelect={trackSelection}
            />
            <UploadDialog
                uploader={uploader ? (file) => uploader(file) : undefined}
                visible={uploadDialogVisible}
                setVisible={setUploadDialogVisible}
                onError={() => setUploadDialogVisible(false)}
                updateContentType={handleUploaded}
            />
            <WordImportDialog
                uploader={uploader ? (blob, fileName, signal) => uploader(blob, {fileName, signal}).then((file) => file.url) : undefined}
                visible={wordImportVisible}
                setVisible={setWordImportVisible}
                onComplete={(markdown) => onChange(markdown)}
                onCancel={() => {}}
            />
            <AiPromptDialog
                visible={aiDialogVisible}
                setVisible={setAiDialogVisible}
                onComplete={handleAiComplete}
            />
        </div>
    );
}

export default MarkdownEditor;